import { computed, shallowRef, toValue } from "vue";

/**
 * @typedef {import("./useFinderFilterRegistry").FinderFilterRegistration} FinderFilterRegistration
 * @typedef {import("#ce/components/SpFinderFilterBar/protocol/contribution-protocol").FinderContribution} FinderContribution
 * @typedef {FinderFilterRegistration | (FinderContribution & {contributionId: string})} FinderMenuItem
 */

/**
 * The menu's view of the bar's registry: which filters it lists, how many the visitor has applied,
 * and the three things it can ask of them.
 *
 * Every value is derived from the two registries, so the menu holds no state of its own and cannot
 * disagree with the bar.
 *
 * @param {import("vue").ShallowRef<Map<HTMLElement, FinderFilterRegistration>>} registry
 * @param {import("vue").ShallowRef<Map<string, FinderContribution>>} [contributions]
 * @param {() => void} [prune] - refresh local DOM membership before acting.
 * @param {import("vue").MaybeRefOrGetter<boolean>} [includeWithheld] - list dependent filters without visible chips.
 * @param {() => Iterable<HTMLElement>} [unregisteredFilters] - slotted filters that have not announced themselves yet.
 * @returns {{
 *   menuItems: import("vue").ComputedRef<FinderMenuItem[]>,
 *   appliedCount: import("vue").ComputedRef<number>,
 *   resettableCount: import("vue").ComputedRef<number>,
 *   open: (menuItem: FinderMenuItem) => void,
 *   clear: (menuItem: FinderMenuItem) => void,
 *   clearAll: () => void,
 *   settleRegistration: (element: HTMLElement) => void,
 *   closeLocalFilters: () => void,
 * }}
 */
export function useFinderFilterMenu(
  registry,
  contributions = shallowRef(new Map()),
  prune = () => {},
  includeWithheld = false,
  unregisteredFilters = () => [],
) {
  /** Filters that had not registered when Reset All ran, cleared once they register. */
  const pendingReset = new WeakSet();

  /**
   * Document order, not the bar's active-first order: the menu is an index of the filters, and a
   * menu item that moves as the visitor applies filters is one they have to hunt for.
   *
   * Contributions precede the local filters in connection order. Withheld contributions are omitted;
   * dependent local filters are listed when the bar opts in.
   */
  const menuItems = computed(() => [...contributionMenuItems(), ...filterMenuItems()]);

  function contributionMenuItems() {
    return [...contributions.value.values()].filter(isListed).map(toContributionMenuItem);
  }

  function filterMenuItems() {
    return [...registry.value.values()].filter(isFilterListed).sort(byDocumentPosition);
  }

  function isFilterListed(entry) {
    return toValue(includeWithheld) || isListed(entry);
  }

  function findCurrentEntry(menuItem) {
    prune();

    const entry = findEntry(menuItem);

    if (entry && (menuItem.contributionId !== undefined ? isListed(entry) : isFilterListed(entry))) {
      return entry;
    }
  }

  function findEntry(menuItem) {
    if (menuItem.contributionId !== undefined) {
      return contributions.value.get(menuItem.contributionId);
    }

    return registry.value.get(menuItem.element);
  }

  /** Local filters count only when removable; external query contributions retain their own applied state. */
  const appliedCount = computed(() => menuItems.value.filter(isCounted).length);

  function isCounted(entry) {
    if (entry.contributionId !== undefined) {
      return isApplied(entry);
    }

    return isClearable(entry);
  }

  /**
   * What Reset All acts on, and whether it appears at all. Required external contributions may
   * still be counted while their owner prevents clearing.
   */
  const resettable = computed(() => menuItems.value.filter(isClearable));

  const resettableCount = computed(() => resettable.value.length);

  /** @param {FinderMenuItem} menuItem */
  function open(menuItem) {
    const entry = findCurrentEntry(menuItem);

    if (!entry) {
      return;
    }

    if (menuItem.contributionId !== undefined) {
      closeLocalFilters();
      entry.actions.reveal?.();

      return;
    }

    entry.actions.open();
  }

  function closeLocalFilters() {
    prune();

    for (const entry of registry.value.values()) {
      if (entry.state.open) {
        entry.actions.close();
      }
    }
  }

  /** @param {FinderMenuItem} menuItem */
  function clear(menuItem) {
    const entry = findCurrentEntry(menuItem);

    if (entry && isClearable(entry)) {
      entry.actions.clear();
    }
  }

  function clearAll() {
    resettable.value.forEach(clear);

    for (const element of unregisteredFilters()) {
      pendingReset.add(element);
    }
  }

  /**
   * Completes a Reset All for a filter that registered after it ran: the filter is cleared when it
   * is clearable. Does nothing for a filter that was registered when Reset All ran.
   *
   * @param {HTMLElement} element
   */
  function settleRegistration(element) {
    if (!pendingReset.has(element)) {
      return;
    }

    pendingReset.delete(element);

    const entry = registry.value.get(element);

    if (entry && isClearable(entry)) {
      entry.actions.clear();
    }
  }

  return { menuItems, appliedCount, resettableCount, open, clear, clearAll, settleRegistration, closeLocalFilters };
}

function isListed(entry) {
  return !entry.state.withheld;
}

function toContributionMenuItem(entry) {
  return { ...entry, id: `contribution:${entry.id}`, contributionId: entry.id };
}

/**
 * Two filters slotted inside one wrapper share the wrapper's `documentIndex`, so the tie is
 * settled by asking the DOM which of the two comes first.
 *
 * @param {FinderFilterRegistration} a
 * @param {FinderFilterRegistration} b
 */
function byDocumentPosition(a, b) {
  if (a.documentIndex !== b.documentIndex) {
    return a.documentIndex - b.documentIndex;
  }

  return a.element.compareDocumentPosition(b.element) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
}

function isApplied(entry) {
  return entry.state.active;
}

function isClearable(entry) {
  return isListed(entry) && isApplied(entry) && entry.state.clearable;
}
